import { addDays, addMonths, compareIsoDate, zonedParts, zoneOffsetMinutes, type IsoDate } from "../../../shared/dates";
import type { CalendarEvent, EventInput } from "../../../shared/models";

export interface PlacedEvent {
  event: CalendarEvent;
  /** First and last civil dates the event covers (inclusive), in the household timezone. */
  firstDate: IsoDate;
  lastDate: IsoDate;
  startMs: number;
  endMs: number;
}

export function placeEvent(e: CalendarEvent, timeZone: string): PlacedEvent {
  if (e.allDay || e.start.date) {
    const first = e.start.date!;
    // Google all-day end dates are exclusive.
    const last = addDays(e.end.date ?? addDays(first, 1), -1);
    return { event: e, firstDate: first, lastDate: compareIsoDate(last, first) < 0 ? first : last, startMs: Date.parse(`${first}T00:00:00Z`), endMs: Date.parse(`${last}T23:59:59Z`) };
  }
  const startMs = Date.parse(e.start.dateTime!);
  const endMs = Date.parse(e.end.dateTime ?? e.start.dateTime!);
  const firstDate = zonedParts(new Date(startMs), timeZone).date;
  // An event ending exactly at midnight doesn't occupy the next day.
  const lastDate = zonedParts(new Date(Math.max(startMs, endMs - 1)), timeZone).date;
  return { event: e, firstDate, lastDate, startMs, endMs };
}

export function eventsOnDay(placed: PlacedEvent[], date: IsoDate): PlacedEvent[] {
  return placed
    .filter((p) => compareIsoDate(p.firstDate, date) <= 0 && compareIsoDate(p.lastDate, date) >= 0)
    .sort((a, b) => {
      const aAll = a.event.allDay ? 0 : 1;
      const bAll = b.event.allDay ? 0 : 1;
      return aAll - bAll || a.startMs - b.startMs || a.event.title.localeCompare(b.event.title);
    });
}

/** Sunday-start weeks covering the whole month containing `anchor`. */
export function monthGrid(anchor: IsoDate): { start: IsoDate; end: IsoDate; weeks: IsoDate[][] } {
  const first = `${anchor.slice(0, 7)}-01`;
  const start = addDays(first, -new Date(`${first}T00:00:00Z`).getUTCDay());
  const last = addDays(addMonths(first, 1), -1);
  const end = addDays(last, 7 - new Date(`${last}T00:00:00Z`).getUTCDay()); // exclusive
  const weeks: IsoDate[][] = [];
  for (let d = start; d < end; d = addDays(d, 7)) weeks.push(Array.from({ length: 7 }, (_, i) => addDays(d, i)));
  return { start, end, weeks };
}

export function weekStart(date: IsoDate): IsoDate {
  return addDays(date, -new Date(`${date}T00:00:00Z`).getUTCDay());
}

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

const hhmm = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

/**
 * An event's times as wall input for the PATCH endpoint, moved by `days`:
 * the same local time of day and length (all-day events stay all-day).
 * `days` = 0 gives its current times, for undoing a move.
 */
export function shiftedTimes(e: CalendarEvent, days: number, timeZone: string): EventInput {
  if (e.allDay || e.start.date) {
    const start = e.start.date!;
    return { allDay: true, startDate: addDays(start, days), endDate: addDays(e.end.date ?? addDays(start, 1), days) };
  }
  const s = zonedParts(new Date(e.start.dateTime!), timeZone);
  const en = zonedParts(new Date(e.end.dateTime ?? e.start.dateTime!), timeZone);
  return { allDay: false, startLocal: `${addDays(s.date, days)}T${hhmm(s.minutes)}`, endLocal: `${addDays(en.date, days)}T${hhmm(en.minutes)}` };
}

/** The event as it will look once moved by `days`, for showing the move before Google confirms it. */
export function shiftedEvent(e: CalendarEvent, days: number, timeZone: string): CalendarEvent {
  const t = shiftedTimes(e, days, timeZone);
  if (t.allDay) return { ...e, start: { ...e.start, date: t.startDate }, end: { ...e.end, date: t.endDate } };
  // Wall time → instant, settling the offset twice so DST days land right.
  const instant = (local: string) => {
    const guess = Date.parse(`${local}:00Z`);
    let ts = guess - zoneOffsetMinutes(new Date(guess), timeZone) * 60_000;
    ts = guess - zoneOffsetMinutes(new Date(ts), timeZone) * 60_000;
    return new Date(ts).toISOString();
  };
  return { ...e, start: { ...e.start, dateTime: instant(t.startLocal!) }, end: { ...e.end, dateTime: instant(t.endLocal!) } };
}

export function fmtDate(date: IsoDate, opts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat("en-US", { ...opts, timeZone: "UTC" }).format(new Date(`${date}T12:00:00Z`));
}
